import React from 'react';
import { hot } from 'react-hot-loader';
import '@emotion/core'; // Only imported to add types
import { Global, GlobalProps } from '@emotion/core';
import { InitBotSite } from '@tomis-tech/types';

import { ThemeProvider } from './components/ThemeProvider';
import { ParentContext } from './context';
import { BotParentPenpalMethods, NumberPair } from './types';
import { getIsMobile } from './view';
import Initialize from './components/Initialize';
import Chat from './components/Chat';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PARENT_PAGE_VIEW } from '.';

type AppProps = {
  /** Methods to interact with parent window */
  parentWindowMethods: BotParentPenpalMethods;
  parentWindowSize: NumberPair;
  /** ID of logged in Firebase user (same as TOMIS device token) */
  firebaseUid: string;
  conversationId: string;
  site: InitBotSite;
};

/** Entry component for React. Include context providers and the whole application */
const App = ({
  parentWindowSize,
  parentWindowMethods,
  firebaseUid,
  conversationId,
  site,
}: AppProps) => {
  const isMobile = getIsMobile(parentWindowSize[0]);

  const parentValue = {
    ...parentWindowMethods,
    parentWindowSize,
    isMobile,
    pageView: PARENT_PAGE_VIEW,
  };

  return (
    <ErrorBoundary>
      <Global styles={globalStyles} />
      <ParentContext.Provider value={parentValue}>
        <Initialize
          firebaseUid={firebaseUid}
          conversationId={conversationId}
          site={site}
        >
          <ThemeProvider>
            <Chat />
          </ThemeProvider>
        </Initialize>
      </ParentContext.Provider>
    </ErrorBoundary>
  );
};

export default hot(module)(App);

const globalStyles: GlobalProps<any>['styles'] = {
  '*': {
    boxSizing: 'border-box',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  '#app-root': {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  'h1,h2,h3,h4,h5,h6,p,ol,ul': {
    margin: 0,
    padding: 0,
    fontWeight: 400,
    letterSpacing: '0.5px',
  },
};
